import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import { DailyLetter, CoupleSettings } from '../types';
import {
  X,
  Heart,
  Sparkles,
  Calendar,
  Share2,
  Check,
  Flower,
  Cake,
  Flame,
  Star,
  Mail,
  Volume2,
  Lock,
  Clock,
  Shield,
  Eye,
  User,
  Smartphone,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { romanticAudio } from '../utils/audio';
import { formatDateSpanish, formatDateTimeSpanish } from '../utils/storage';
import { isLetterSealed, getLetterCountdown } from '../utils/letterLockUtils';
import { GrandFloralBouquet } from './GrandFloralBouquet';
import { resolveLetterViewer } from '../utils/letterAudit';

interface DailyLetterModalProps {
  letter: DailyLetter | null;
  settings: CoupleSettings;
  isAdmin?: boolean;
  onClose: () => void;
  onToggleFavorite?: (letterId: string) => void;
  onRecordOpen?: (letter: DailyLetter, viewerName: string, deviceModel?: string) => void;
  currentVisitor?: any;
}

export const DailyLetterModal: React.FC<DailyLetterModalProps> = ({
  letter,
  settings,
  isAdmin = false,
  onClose,
  onToggleFavorite,
  onRecordOpen,
  currentVisitor,
}) => {
  const [isEnvelopeOpen, setIsEnvelopeOpen] = useState(false);
  const [candlesLit, setCandlesLit] = useState(true);
  const [isWishMade, setIsWishMade] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [countdown, setCountdown] = useState(() => getLetterCountdown(letter));
  const [hasRecordedOpen, setHasRecordedOpen] = useState(false);
  const [showAuditHistory, setShowAuditHistory] = useState(false);

  const isLocked = isLetterSealed(letter);

  const getViewerName = () => {
    const profile = typeof window !== 'undefined' ? localStorage.getItem('couple_active_profile') : null;
    if (profile === 'novia') return settings.partner2Name || settings.partner2Title || 'Novia';
    if (profile === 'novio') return settings.partner1Name || settings.partner1Title || 'Novio';
    return settings.partner2Name || 'Lector';
  };

  useEffect(() => {
    if (letter) {
      setIsEnvelopeOpen(false);
      setCandlesLit(true);
      setIsWishMade(false);
      setCopiedLink(false);
      setIsFavorited(Boolean(letter.isFavorite));
      setCountdown(getLetterCountdown(letter));
      setHasRecordedOpen(false);
      setShowAuditHistory(false);
    }
  }, [letter]);

  // Temporizador en vivo para la cuenta regresiva del sobre sellado
  useEffect(() => {
    if (!letter || !isLocked) return;

    const timer = setInterval(() => {
      const currentCountdown = getLetterCountdown(letter);
      setCountdown(currentCountdown);
    }, 1000);

    return () => clearInterval(timer);
  }, [letter, isLocked]);

  if (!letter) return null;

  // Handle open envelope with audio and festive burst (sólo si no está sellada)
  const handleOpenEnvelope = () => {
    if (isLocked) {
      romanticAudio.playNote(330, 0.4, 'sine', 0.05);
      return;
    }

    romanticAudio.playBloomSuccess();
    setIsEnvelopeOpen(true);

    // Record letter open (fecha y hora exacta y quién la ha visto mediante IP y dispositivo)
    if (!hasRecordedOpen && letter && onRecordOpen) {
      setHasRecordedOpen(true);
      const resolved = resolveLetterViewer(currentVisitor, settings, letter);
      onRecordOpen(letter, resolved.viewerName, resolved.deviceModel);
    }

    // Trigger romantic confetti blast
    confetti({
      particleCount: letter.category === 'yellow-flowers' ? 60 : 45,
      spread: 80,
      origin: { y: 0.6, x: 0.5 },
      colors:
        letter.category === 'yellow-flowers'
          ? ['#FDE047', '#F59E0B', '#FBBF24', '#FFFFFF', '#FEF08A']
          : letter.category === 'birthday'
          ? ['#FB7185', '#F43F5E', '#FDA4AF', '#FDE047', '#FFFFFF']
          : ['#F59E0B', '#EC4899', '#8B5CF6', '#FFFFFF'],
    });
  };

  // Bouquet interaction click
  const handleBouquetClick = () => {
    romanticAudio.playNote(587.33, 0.7, 'sine', 0.08);
    confetti({
      particleCount: 50,
      spread: 80,
      origin: { y: 0.5, x: 0.5 },
      colors: ['#FDE047', '#F59E0B', '#FBBF24', '#FFFFFF', '#FEF08A'],
    });
  };

  // Blow out birthday candles
  const handleBlowCandles = () => {
    romanticAudio.playChime();
    setCandlesLit(false);
    setIsWishMade(true);

    confetti({
      particleCount: 80,
      spread: 100,
      origin: { y: 0.45, x: 0.5 },
      colors: ['#F43F5E', '#FB7185', '#FDE047', '#38BDF8', '#A855F7'],
    });
  };

  // Fireworks celebration interaction
  const handleFireworks = () => {
    romanticAudio.playBloomSuccess();
    const count = 250;
    const defaults = { origin: { y: 0.7 } };

    function fire(particleRatio: number, opts: confetti.Options) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55, colors: ['#F59E0B', '#FBBF24', '#FDE047'] });
    fire(0.2, { spread: 60, colors: ['#EC4899', '#8B5CF6', '#3B82F6'] });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, colors: ['#10B981', '#F43F5E', '#FBBF24'], scalar: 1.2 });
  };

  // Floating hearts interaction
  const handleHearts = () => {
    romanticAudio.playChime();
    confetti({
      particleCount: 40,
      spread: 70,
      origin: { y: 0.6, x: 0.5 },
      colors: ['#EC4899', '#F43F5E', '#FDA4AF', '#FB7185'],
    });
  };

  // Toggle favorite
  const handleToggleFav = () => {
    setIsFavorited(!isFavorited);
    if (onToggleFavorite) onToggleFavorite(letter.id);
    romanticAudio.playNote(659.25, 0.4, 'sine', 0.05);
  };

  // Copy share message
  const handleCopy = () => {
    const text = `💌 ${letter.title} - ${settings.partner1Name} & ${settings.partner2Name}\n${letter.subtitle}\n${window.location.origin}/cartas`;
    navigator.clipboard?.writeText(text);
    setCopiedLink(true);
    romanticAudio.playNote(523.25, 0.4, 'sine', 0.05);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  // Category badge text
  const getCategoryLabel = () => {
    switch (letter.category) {
      case 'daily':
        return 'Carta del Día ☀️';
      case 'morning':
        return 'Buenos Días 🌅';
      case 'night':
        return 'Buenas Noches 🌙';
      case 'love':
        return 'Carta de Amor 💖';
      case 'gratitude':
        return 'Agradecimiento ✨';
      case 'yellow-flowers':
        return 'Flores Amarillas 🌻';
      case 'anniversary':
        return 'Aniversario 🥂';
      case 'birthday':
        return 'Cumpleaños 🎂';
      default:
        return letter.customCategoryName || 'Carta Especial 💌';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto backdrop-blur-md bg-slate-900/35 animate-fadeIn select-none">
        {/* Modal Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0"
        />

        {/* Modal Content Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 16 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-xl bg-[#FAF8F5] border border-stone-200/90 rounded-[2rem] sm:rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden text-stone-900 z-10 my-auto"
          id="daily-letter-modal-card"
        >
          {/* Top Decorative Header Accent */}
          <div className="relative pt-6 px-5 sm:px-8 pb-4 bg-gradient-to-b from-stone-100/80 via-[#FAF8F5] to-[#FAF8F5] border-b border-stone-200/60">
            {letter.coverImage && (
              <div className="relative h-36 sm:h-44 -mx-5 sm:-mx-8 -mt-6 mb-4 overflow-hidden bg-stone-100">
                {/* Ambient Blurred Backdrop */}
                <div
                  className="absolute inset-0 bg-cover bg-center blur-md opacity-40 scale-110 pointer-events-none"
                  style={{ backgroundImage: `url(${letter.coverImage})` }}
                />
                <img
                  src={letter.coverImage}
                  alt={letter.title}
                  className="relative w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F5] via-transparent to-black/30" />
              </div>
            )}

            {/* Top Navigation Row: Category Pill, Date, Favorite & Close */}
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase bg-rose-50 border border-rose-200 text-rose-700 shadow-2xs">
                  <Mail className="w-3.5 h-3.5 text-rose-500" />
                  <span>{getCategoryLabel()}</span>
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/90 border border-stone-200 text-stone-600 shadow-2xs">
                  <Calendar className="w-3 h-3 text-stone-400" />
                  <span>{formatDateSpanish(letter.date)}</span>
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                {/* Favorite button */}
                <button
                  type="button"
                  onClick={handleToggleFav}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                    isFavorited
                      ? 'bg-amber-100 text-amber-600 border border-amber-300 shadow-xs'
                      : 'bg-white/80 hover:bg-white text-stone-400 hover:text-amber-500 border border-stone-200 shadow-2xs'
                  }`}
                  title={isFavorited ? 'Favorita' : 'Marcar como favorita'}
                  id="btn-fav-letter-modal"
                >
                  <Star className={`w-4 h-4 ${isFavorited ? 'fill-amber-500 text-amber-500' : ''}`} />
                </button>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-9 h-9 rounded-full bg-white/80 hover:bg-white text-stone-500 hover:text-stone-900 border border-stone-200 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
                  id="btn-close-letter-modal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Title & Subtitle */}
            <div className="space-y-1">
              <h2 className="font-['Playfair_Display',serif] text-2xl sm:text-3xl font-extrabold text-stone-900 leading-tight tracking-tight">
                {letter.title}
              </h2>
              {letter.subtitle && (
                <p className="text-xs sm:text-sm text-stone-500 font-medium italic">
                  "{letter.subtitle}"
                </p>
              )}
            </div>
          </div>

          {/* Body Section */}
          <div className="p-4 sm:p-7 max-h-[68vh] overflow-y-auto space-y-6">
            {isLocked ? (
              /* STRICTLY LOCKED TIME-CAPSULE VIEW */
              <div className="flex flex-col items-center justify-center text-center py-6 px-2 space-y-5">
                <motion.div
                  initial={{ scale: 0.96, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="relative w-full max-w-sm bg-gradient-to-br from-[#FFFDF9] via-[#FAF5EC] to-[#F3EDE2] border-2 border-amber-300/80 rounded-3xl p-6 shadow-[0_12px_32px_-8px_rgba(217,119,6,0.18)] flex flex-col items-center justify-center overflow-hidden"
                >
                  {/* Subtle sealed lock aura */}
                  <div className="absolute inset-0 bg-radial from-amber-400/10 via-transparent to-transparent pointer-events-none" />

                  {/* Golden Wax Seal with Lock */}
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 2.8, ease: 'easeInOut' }}
                    className="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-600 via-rose-600 to-amber-500 border-2 border-white shadow-lg flex items-center justify-center text-white z-10"
                  >
                    <Lock className="w-7 h-7 text-amber-100 drop-shadow-md" />
                  </motion.div>

                  <div className="mt-4 flex flex-col items-center z-10">
                    <span className="text-xs font-bold text-amber-900 tracking-wider uppercase font-mono flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-amber-600" />
                      Cápsula del Tiempo Sellada
                    </span>
                    <span className="text-xs text-rose-700 font-medium mt-1">
                      Se abre el {countdown.formattedTargetDate}
                    </span>
                  </div>
                </motion.div>

                {/* Countdown display */}
                <div className="w-full max-w-sm flex flex-col items-center gap-2.5">
                  <div className="flex items-center gap-1.5 text-xs text-amber-800 font-medium">
                    <Clock className="w-3.5 h-3.5 animate-pulse text-amber-600" />
                    <span>Tiempo restante para su apertura:</span>
                  </div>

                  <div className="grid grid-cols-4 gap-2 w-full">
                    <div className="p-3 rounded-2xl bg-white border border-stone-200/90 text-center shadow-xs">
                      <div className="text-xl sm:text-2xl font-bold font-mono text-stone-900">
                        {String(countdown.days).padStart(2, '0')}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mt-0.5">
                        Días
                      </div>
                    </div>
                    <div className="p-3 rounded-2xl bg-white border border-stone-200/90 text-center shadow-xs">
                      <div className="text-xl sm:text-2xl font-bold font-mono text-stone-900">
                        {String(countdown.hours).padStart(2, '0')}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mt-0.5">
                        Horas
                      </div>
                    </div>
                    <div className="p-3 rounded-2xl bg-white border border-stone-200/90 text-center shadow-xs">
                      <div className="text-xl sm:text-2xl font-bold font-mono text-stone-900">
                        {String(countdown.minutes).padStart(2, '0')}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mt-0.5">
                        Min
                      </div>
                    </div>
                    <div className="p-3 rounded-2xl bg-white border border-stone-200/90 text-center shadow-xs">
                      <div className="text-xl sm:text-2xl font-bold font-mono text-stone-900">
                        {String(countdown.seconds).padStart(2, '0')}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mt-0.5">
                        Seg
                      </div>
                    </div>
                  </div>
                </div>

                {/* Romantic explanation card */}
                <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 max-w-md text-center space-y-1.5 shadow-2xs">
                  <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-amber-900">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span>Promesa de Amor Resguardada</span>
                  </div>
                  <p className="text-xs text-stone-600 leading-relaxed">
                    Esta carta fue sellada con promesa de amor. Permanecerá estrictamente bloqueada hasta el día acordado ({countdown.formattedTargetDate}). ¡La espera hace que cada palabra sea aún más mágica!
                  </p>
                </div>
              </div>
            ) : !isEnvelopeOpen ? (
              /* MODERN ELEGANT ENVELOPE INTERACTIVE COVER */
              <div className="flex flex-col items-center justify-center text-center py-4 sm:py-6 px-2">
                <motion.div
                  whileHover={{ scale: 1.025, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleOpenEnvelope}
                  className="relative w-full max-w-sm h-52 sm:h-56 bg-gradient-to-br from-[#FFFDF9] via-[#FAF5EC] to-[#F1EAE0] border border-amber-900/15 rounded-3xl p-6 shadow-[0_16px_36px_-10px_rgba(180,140,100,0.22)] flex flex-col items-center justify-center cursor-pointer group transition-all"
                  id="interactive-envelope-trigger"
                >
                  {/* Decorative envelope fold origami flap */}
                  <div className="absolute top-0 inset-x-0 h-1/2 border-b border-amber-900/10 pointer-events-none rounded-t-3xl bg-gradient-to-b from-white/60 to-transparent" />

                  {/* 3D Wax Seal with Pulse */}
                  <motion.div
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                    className="w-16 h-16 rounded-full bg-gradient-to-tr from-rose-700 via-rose-600 to-amber-600 border-2 border-white shadow-[0_8px_24px_rgba(225,29,72,0.35)] flex items-center justify-center text-white z-10 group-hover:rotate-6 transition-transform"
                  >
                    {letter.waxSealIcon === 'sun' ? (
                      <Sparkles className="w-7 h-7 fill-white drop-shadow-sm" />
                    ) : letter.waxSealIcon === 'flower' ? (
                      <Flower className="w-7 h-7 fill-white drop-shadow-sm" />
                    ) : letter.waxSealIcon === 'star' ? (
                      <Star className="w-7 h-7 fill-white drop-shadow-sm" />
                    ) : (
                      <Heart className="w-7 h-7 fill-white drop-shadow-sm animate-pulse" />
                    )}
                  </motion.div>

                  <span className="mt-4 text-xs sm:text-[13px] font-bold text-stone-800 tracking-widest uppercase font-['Playfair_Display',serif] group-hover:text-rose-600 transition-colors">
                    Toca para abrir la carta
                  </span>
                  <span className="text-xs text-stone-500 italic mt-1">
                    De: <strong className="text-stone-700">{letter.letterContent.senderName || settings.partner1Name}</strong>{' '}
                    <span className="text-rose-500 font-normal">&amp;</span> Para:{' '}
                    <strong className="text-stone-700">{letter.letterContent.recipientName || settings.partner2Name}</strong>
                  </span>
                </motion.div>

                <p className="text-xs text-stone-500 mt-4 font-medium flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>Haz clic sobre el sobre para desplegar la carta de amor y su sorpresa.</span>
                </p>
              </div>
            ) : (
              /* UNLOCKED & OPENED LETTER CONTENT VIEW */
              <div className="space-y-6 animate-fadeIn">
                {/* 1. MODERN LUMINOUS CELEBRATION COMPONENT */}
                {(letter.interactiveSurprise?.type === 'bouquet-yellow' || letter.category === 'yellow-flowers') && (
                  <GrandFloralBouquet
                    recipientName={letter.letterContent.recipientName || settings.partner2Name}
                    senderName={letter.letterContent.senderName || settings.partner1Name}
                    onInteract={handleBouquetClick}
                  />
                )}

                {(letter.interactiveSurprise?.type === 'birthday-cake' || letter.category === 'birthday') && (
                  <div className="relative bg-gradient-to-r from-rose-50 via-pink-50 to-amber-50 border border-rose-200/80 rounded-3xl p-5 sm:p-6 text-center shadow-xs">
                    <div className="flex flex-col items-center justify-center my-1">
                      {/* Candles Flames */}
                      <div className="flex items-center gap-3 mb-1">
                        {[0, 1, 2].map((i) => (
                          <div key={i} className="flex flex-col items-center">
                            {candlesLit ? (
                              <motion.div
                                animate={{ scale: [1, 1.25, 0.9, 1.2], opacity: [0.8, 1, 0.9] }}
                                transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }}
                                className="text-amber-500 drop-shadow-sm"
                              >
                                <Flame className="w-5 h-5 fill-amber-400 text-amber-500" />
                              </motion.div>
                            ) : (
                              <motion.div
                                initial={{ opacity: 0.8, y: 0 }}
                                animate={{ opacity: 0, y: -10 }}
                                className="w-1.5 h-1.5 rounded-full bg-stone-400"
                              />
                            )}
                            <div className="w-1 h-3 bg-gradient-to-b from-rose-400 to-pink-500 rounded-full" />
                          </div>
                        ))}
                      </div>

                      {/* Cake layers */}
                      <div className="w-28 sm:w-32 h-10 bg-gradient-to-r from-pink-300 via-rose-200 to-pink-300 rounded-t-xl border-t border-x border-rose-200 flex items-center justify-center shadow-xs">
                        <span className="text-xs">🍓 🍰 🍓</span>
                      </div>
                      <div className="w-36 sm:w-40 h-8 bg-gradient-to-r from-rose-500 via-pink-500 to-rose-500 rounded-b-xl border border-rose-400 flex items-center justify-center shadow-sm">
                        <span className="text-[10px] text-white font-bold tracking-widest">
                          ✨ FELIZ CUMPLEAÑOS ✨
                        </span>
                      </div>
                    </div>

                    {candlesLit ? (
                      <button
                        type="button"
                        onClick={handleBlowCandles}
                        className="mt-3 px-5 py-2 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white text-xs font-bold rounded-full shadow-md shadow-rose-500/20 transition-all flex items-center gap-1.5 mx-auto cursor-pointer active:scale-95"
                      >
                        <Flame className="w-3.5 h-3.5" />
                        <span>Soplar velitas y pedir un deseo 🎂</span>
                      </button>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-2 text-xs text-rose-700 font-bold"
                      >
                        ✨ ¡Deseo enviado al universo! Que todos tus sueños se cumplan. ✨
                      </motion.div>
                    )}
                  </div>
                )}

                {(letter.interactiveSurprise?.type === 'fireworks' || letter.category === 'anniversary') && (
                  <div
                    onClick={handleFireworks}
                    className="relative bg-gradient-to-r from-amber-50 via-rose-50 to-purple-50/60 border border-amber-200/80 rounded-3xl p-5 text-center cursor-pointer hover:border-amber-300 hover:shadow-md transition-all shadow-xs group"
                  >
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.2 }} className="text-3xl">
                        🎆
                      </motion.span>
                      <motion.span animate={{ rotate: [0, 15, -15, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="text-3xl">
                        ✨
                      </motion.span>
                      <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.2, delay: 0.3 }} className="text-3xl">
                        🥂
                      </motion.span>
                    </div>
                    <h4 className="text-sm font-bold text-stone-900 font-['Playfair_Display',serif]">
                      Fuegos Artificiales de Celebración
                    </h4>
                    <p className="text-xs text-stone-600 font-['Cormorant_Garamond',serif] italic mt-0.5">
                      "Brindemos por cada paso recorrido y por todo lo hermoso que nos falta vivir."
                    </p>
                    <div className="mt-2.5 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/90 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wide uppercase shadow-2xs group-hover:bg-amber-50 transition-colors">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span>Toca para encender los fuegos artificiales 🎆✨</span>
                    </div>
                  </div>
                )}

                {(letter.interactiveSurprise?.type === 'hearts' || letter.category === 'night' || letter.category === 'gratitude') && (
                  <div
                    onClick={handleHearts}
                    className="relative bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50 border border-purple-200/80 rounded-3xl p-5 text-center cursor-pointer hover:border-purple-300 hover:shadow-md transition-all shadow-xs group"
                  >
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} className="text-3xl">
                        💜
                      </motion.span>
                      <motion.span animate={{ rotate: [0, 15, -15, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="text-3xl">
                        ✨
                      </motion.span>
                      <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.5, delay: 0.2 }} className="text-3xl">
                        🌙
                      </motion.span>
                    </div>
                    <h4 className="text-sm font-bold text-stone-900 font-['Playfair_Display',serif]">
                      Estrellas & Corazones Mágicos
                    </h4>
                    <p className="text-xs text-stone-600 font-['Cormorant_Garamond',serif] italic mt-0.5">
                      "Cada latido de mi corazón lleva tu nombre grabado."
                    </p>
                    <div className="mt-2.5 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/90 border border-purple-200 text-purple-800 text-[10px] font-bold tracking-wide uppercase shadow-2xs group-hover:bg-purple-50 transition-colors">
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      <span>Toca para activar la magia ✨💜</span>
                    </div>
                  </div>
                )}

                {/* 2. OPTIONAL ATTACHED PHOTOGRAPH */}
                {letter.coverImage && (
                  <div className="relative w-full rounded-2xl overflow-hidden shadow-sm border border-stone-200/80 bg-white group">
                    <div className="relative flex items-center justify-center p-2">
                      <img
                        src={letter.coverImage}
                        alt={letter.title}
                        className="w-full max-h-[380px] object-contain rounded-xl transition-transform duration-500 group-hover:scale-[1.01]"
                      />
                    </div>
                    <div className="absolute bottom-3 right-4 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-md text-[11px] text-stone-700 border border-stone-200 shadow-2xs flex items-center gap-1 pointer-events-none">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span>Fotografía del Recuerdo</span>
                    </div>
                  </div>
                )}

                {/* 3. EXQUISITE EDITORIAL HANDWRITTEN STATIONERY PAPER */}
                <div
                  className="relative rounded-3xl p-6 sm:p-9 md:p-11 shadow-[0_15px_35px_-10px_rgba(0,0,0,0.06)] border border-stone-200/90 overflow-hidden text-stone-900 select-text bg-[#FFFDF9]"
                  style={{
                    boxShadow: '0 15px 35px -10px rgba(0,0,0,0.06), inset 0 0 40px rgba(240, 230, 215, 0.2)',
                  }}
                >
                  {/* Subtle writing lines pattern */}
                  <div
                    className="absolute inset-0 pointer-events-none opacity-30"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, rgba(180, 140, 100, 0.18) 32px)',
                      backgroundPosition: '0 52px',
                    }}
                  />

                  {/* Authentic soft rose margin guide line */}
                  <div className="absolute top-0 bottom-0 left-7 sm:left-11 w-[1.5px] bg-rose-300/40 pointer-events-none" />

                  {/* Modern washi tape holding the top of the paper */}
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-100/80 backdrop-blur-xs border border-amber-200/80 rotate-[-1deg] shadow-2xs flex items-center justify-center pointer-events-none z-10 rounded-sm">
                    <span className="text-[10px] font-mono tracking-wider text-amber-900/80 uppercase font-semibold">
                      {formatDateSpanish(letter.date)}
                    </span>
                  </div>

                  {/* Paper watermark stamp */}
                  <div className="absolute top-4 right-5 text-3xl opacity-10 pointer-events-none select-none rotate-12">
                    💌
                  </div>

                  {/* Letter content with genuine handwritten ink styling */}
                  <div className="relative pl-5 sm:pl-9 space-y-4 z-10">
                    {/* Salutation */}
                    <div className="pb-1">
                      <p className="font-['Caveat',cursive] text-2xl sm:text-3xl md:text-[32px] text-stone-900 font-bold tracking-wide">
                        {letter.letterContent.openingSalutation ||
                          `Para ${letter.letterContent.recipientName || settings.partner2Name}:`}
                      </p>
                    </div>

                    {/* Body Paragraphs in handwriting */}
                    <div className="space-y-4 font-['Caveat',cursive] text-xl sm:text-2xl md:text-[25px] leading-[34px] text-stone-800 tracking-wide antialiased">
                      {letter.letterContent.bodyParagraphs.map((p, idx) => (
                        <p key={idx} className="whitespace-pre-line text-stone-800">
                          {p}
                        </p>
                      ))}
                    </div>

                    {/* Letter Closing & Handwritten Signature */}
                    <div className="pt-6 mt-4 flex flex-col items-end text-right font-['Caveat',cursive]">
                      <p className="text-xl sm:text-2xl text-stone-600 italic">
                        {letter.letterContent.closingSalutation || 'Por siempre tuyo,'}
                      </p>
                      <p className="font-['Dancing_Script',cursive] text-2xl sm:text-3xl md:text-4xl font-bold text-rose-700 mt-1 tracking-wider rotate-[-2deg]">
                        {letter.letterContent.signature ||
                          letter.letterContent.senderName ||
                          settings.partner1Name}
                      </p>
                    </div>

                    {/* Post Scriptum (P.D.) */}
                    {letter.letterContent.pSMessage && (
                      <div className="mt-5 pt-3 border-t border-stone-200/80 font-['Caveat',cursive] text-lg sm:text-xl text-rose-800 tracking-wide">
                        <span className="font-bold mr-1.5 text-rose-700">P.D.:</span>
                        <span className="italic">{letter.letterContent.pSMessage}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Audit: Registro de Lectura y Apertura (Solo visible para Administrador) */}
                {isAdmin && (
                  <div className="mt-4 p-4 rounded-2xl bg-white border border-stone-200/90 shadow-xs text-stone-700 text-xs">
                    <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-700 border border-amber-200/60 flex items-center justify-center shrink-0">
                          <Eye className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-semibold text-stone-900 flex items-center gap-1.5">
                            <span>Registro de Lectura</span>
                            {letter.isPrivate ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                                <Lock className="w-2.5 h-2.5" /> Privada
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                🌐 Pública
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-stone-500 mt-0.5">
                            Visto por: <strong className="text-stone-800">{letter.openedBy || getViewerName()}</strong>
                          </div>
                        </div>
                      </div>
                      <div className="text-left sm:text-right font-mono text-[11px] text-stone-600">
                        <div className="text-[10px] text-stone-400 font-sans">Fecha y hora de apertura:</div>
                        <div>{formatDateTimeSpanish(letter.lastOpenedAt || letter.firstOpenedAt || new Date().toISOString())}</div>
                      </div>
                    </div>

                    {letter.views && letter.views.length > 1 && (
                      <div className="mt-3 pt-2.5 border-t border-stone-100 flex items-center justify-between text-[11px] text-stone-500">
                        <span>Total de visualizaciones: {letter.views.length}</span>
                        <button
                          type="button"
                          onClick={() => setShowAuditHistory(!showAuditHistory)}
                          className="text-rose-600 hover:text-rose-700 flex items-center gap-1 font-medium cursor-pointer"
                        >
                          <span>{showAuditHistory ? 'Ocultar historial' : 'Ver todas las lecturas'}</span>
                          {showAuditHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}

                    {showAuditHistory && letter.views && (
                      <div className="mt-2 space-y-1.5 max-h-36 overflow-y-auto pr-1">
                        {letter.views.map((v, i) => (
                          <div
                            key={i}
                            className="p-2 rounded-xl bg-stone-50 border border-stone-200 text-[11px] flex items-center justify-between gap-2"
                          >
                            <span className="text-stone-700">
                              👤 {v.viewerName} {v.deviceModel ? `(${v.deviceModel})` : ''}
                            </span>
                            <span className="font-mono text-stone-500 shrink-0">
                              {formatDateTimeSpanish(v.openedAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Clean Modern Footer Bar */}
          <div className="p-4 sm:p-5 bg-stone-100/70 border-t border-stone-200/80 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleCopy}
              className="text-xs text-stone-600 hover:text-stone-900 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white hover:bg-stone-50 transition-all border border-stone-200 shadow-2xs cursor-pointer"
            >
              {copiedLink ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-emerald-700 font-semibold">¡Copiado!</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5 text-stone-500" />
                  <span>Compartir Carta</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-[#E85D8E] to-[#FF8DAF] hover:from-[#DF4D80] hover:to-[#FF7A9F] text-white font-bold text-xs rounded-full transition-all shadow-md shadow-[#E85D8E]/20 cursor-pointer"
            >
              Cerrar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
